import {
  getFormattedCustomerTransaction,
  getFormattedSupplierTransaction
} from 'state/transaction/helpers/formatter'
import { sortByBillDateAndCreatedTime } from 'pages/Transactions/components/TransactionReport/TransactionReport.helper'
import {
  ICustomerTransaction,
  ISupplierTransaction,
  TRANSACTION_CATEGORY,
  TRANSACTION_STATE,
  TRANSACTION_TYPE
} from 'state/transaction/transaction.types'
import { ENTITY_TYPE } from '../../constants'

export interface IAccountReportAccount {
  id: string
  name: string
  /** Current net balance in rupees, signed. Negative = due to merchant, positive = advance. */
  balance: number
  mobile?: string
  /** Whether the account itself has been deleted (its transactions are still shown). */
  deleted?: boolean
}

export type IAccountReportRow = (ICustomerTransaction | ISupplierTransaction) & {
  accountId: string
  accountName: string
  accountMobile?: string
  accountDeleted: boolean
  outStandingAmount: number
}

export interface IAccountReportSummaryItem {
  id: string
  name: string
  balance: number
  deleted: boolean
  paymentCount: number
  creditCount: number
  payment: number
  credit: number
  /** Discount-category payments, tracked separately from payments (Android parity). */
  discountCount: number
  discount: number
}

export interface IAccountReport {
  entityType: ENTITY_TYPE
  /** Per-account rows (summary view) sorted by absolute balance, largest first. */
  summary: IAccountReportSummaryItem[]
  /** Merged, date-descending transaction rows across all accounts (ledger view). */
  transactions: IAccountReportRow[]
  paymentCount: number
  creditCount: number
  payment: number
  credit: number
  /** Discount-category payments, tracked separately from payments (Android parity). */
  discountCount: number
  discount: number
  /** Sum of every account's balance (Android parity: currentBalance). */
  combinedBalance: number
  accountsCount: number
}

export interface IBuildAccountReportArgs {
  entityType: ENTITY_TYPE
  accounts: IAccountReportAccount[]
  /** Raw API transactions (as stored in IndexedDB) keyed by accountId. */
  transactionsByAccount: Record<string, any[]>
  /** Optional date window (epoch seconds). 0/undefined start = from the beginning. */
  filter?: { startTime?: number; endTime?: number }
}

/** Excludes deleted + still-processing transactions, mirroring Android's filterTransactions. */
const isReportable = (txn: any): boolean =>
  !txn.deleted && Number(txn.transaction_state) !== TRANSACTION_STATE.PROCESSING

const withinFilter = (
  billedTime: number,
  filter?: { startTime?: number; endTime?: number }
): boolean => {
  if (!filter) {
    return true
  }
  const { startTime, endTime } = filter
  if (startTime && billedTime < startTime) {
    return false
  }
  if (endTime && billedTime > endTime) {
    return false
  }
  return true
}

/**
 * Anchors each transaction's running balance to the account's current net balance:
 * the newest transaction shows the current balance, and we walk backwards removing
 * each transaction's effect (payment raises the balance, credit lowers it) to get
 * the balance as of every earlier transaction. `rows` must be oldest → newest.
 */
const applyRunningBalance = <T extends ICustomerTransaction | ISupplierTransaction>(
  rows: T[],
  currentBalance: number
): (T & { outStandingAmount: number })[] => {
  let running = currentBalance
  for (let i = rows.length - 1; i >= 0; i--) {
    const row = rows[i] as T & { outStandingAmount: number }
    row.outStandingAmount = running
    const effect = row.transactionType === TRANSACTION_TYPE.PAYMENT ? row.amount : -row.amount
    running -= effect
  }
  return rows as (T & { outStandingAmount: number })[]
}

/**
 * Builds the consolidated account report from cached transactions and the account
 * list. Pure (no DB / network) so it can be unit-tested directly.
 *
 * Per-account running balances are anchored to each account's current net balance
 * (newest transaction = current balance, walking backwards), computed over the
 * account's full history; the date filter then restricts which rows are shown and
 * which transactions feed the totals.
 */
export const buildAccountReport = ({
  entityType,
  accounts,
  transactionsByAccount,
  filter
}: IBuildAccountReportArgs): IAccountReport => {
  const isCustomer = entityType === ENTITY_TYPE.CUSTOMER
  const format = isCustomer ? getFormattedCustomerTransaction : getFormattedSupplierTransaction

  const summary: IAccountReportSummaryItem[] = []
  const mergedRows: IAccountReportRow[] = []

  let paymentCount = 0
  let creditCount = 0
  let payment = 0
  let credit = 0
  let discountCount = 0
  let discount = 0
  let combinedBalance = 0

  for (const account of accounts) {
    combinedBalance += Number(account.balance) || 0

    const raw = (transactionsByAccount[account.id] || []).filter(isReportable)
    // Oldest → newest so the anchored running-balance walk lines up.
    const formatted = sortByBillDateAndCreatedTime(raw.map((txn) => format(txn))) as (
      | ICustomerTransaction
      | ISupplierTransaction
    )[]
    const withBalance = applyRunningBalance(
      formatted,
      Number(account.balance) || 0
    ) as IAccountReportRow[]

    let acctPaymentCount = 0
    let acctCreditCount = 0
    let acctPayment = 0
    let acctCredit = 0
    let acctDiscountCount = 0
    let acctDiscount = 0

    for (const row of withBalance) {
      if (!withinFilter(row.billedTime, filter)) {
        continue
      }
      row.accountId = account.id
      row.accountName = account.name
      row.accountMobile = account.mobile
      row.accountDeleted = !!account.deleted
      mergedRows.push(row)

      if (row.transactionType === TRANSACTION_TYPE.PAYMENT) {
        // Discounts are PAYMENT-type but reported separately (Android parity):
        // they affect the running balance like a payment, but are excluded from
        // the payment totals and surfaced on their own "Discounts offered" line.
        if (row.transactionCategory === TRANSACTION_CATEGORY.DISCOUNT) {
          acctDiscountCount++
          acctDiscount += row.amount
        } else {
          acctPaymentCount++
          acctPayment += row.amount
        }
      } else {
        acctCreditCount++
        acctCredit += row.amount
      }
    }

    paymentCount += acctPaymentCount
    creditCount += acctCreditCount
    payment += acctPayment
    credit += acctCredit
    discountCount += acctDiscountCount
    discount += acctDiscount

    summary.push({
      id: account.id,
      name: account.name,
      balance: Number(account.balance) || 0,
      deleted: !!account.deleted,
      paymentCount: acctPaymentCount,
      creditCount: acctCreditCount,
      payment: acctPayment,
      credit: acctCredit,
      discountCount: acctDiscountCount,
      discount: acctDiscount
    })
  }

  // Ledger view: most recent first.
  mergedRows.sort((a, b) => {
    const dateDiff = b.billedTime - a.billedTime
    return dateDiff !== 0 ? dateDiff : b.createdTime - a.createdTime
  })

  // Summary view: biggest balances first.
  summary.sort((a, b) => Math.abs(b.balance) - Math.abs(a.balance))

  return {
    entityType,
    summary,
    transactions: mergedRows,
    paymentCount,
    creditCount,
    payment,
    credit,
    discountCount,
    discount,
    combinedBalance,
    accountsCount: accounts.length
  }
}
